import { cn } from '../../../lib/utils';

const STATE_STYLES = {
  hit: 'border-transparent bg-[hsl(var(--winner))] text-white',
  near: 'border-transparent bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]',
  miss: 'border-transparent bg-muted text-muted-foreground',
  reveal: 'border-transparent bg-heat/80 text-heat-foreground',
  filled: 'border-foreground/60 text-foreground',
  empty: 'border-border/60 text-transparent',
};

const isSeparator = (ch) => ch === ' ';

const Tile = ({ char, state, animate, delay }) => {
  if (char === ' ') return <span aria-hidden />;

  const style = STATE_STYLES[state] || STATE_STYLES.empty;
  const flipping = animate && state !== 'empty' && state !== 'filled';

  return (
    <span
      style={flipping ? { animationDelay: `${delay}ms` } : undefined}
      className={cn(
        'relative aspect-square w-full select-none rounded-sm border-2 font-display font-semibold uppercase tabular-nums transition-colors',
        style,
        flipping && 'animate-tile-flip',
        state === 'filled' && 'animate-tile-pop'
      )}
    >
      <span
        className="absolute inset-0 flex items-center justify-center"
        style={{ fontSize: 'clamp(0.9rem, 4.5vw, 1.75rem)', lineHeight: 1 }}
      >
        {char || ''}
      </span>
    </span>
  );
};

// Compute per-letter state for a submitted guess vs target.
// Returns array of 'hit' | 'near' | 'miss' for scoreable positions; separator passthrough.
export const scoreGuess = (guess, target) => {
  const t = target.toUpperCase().split('');
  const g = guess.toUpperCase().padEnd(t.length, ' ').split('').slice(0, t.length);
  const result = new Array(t.length).fill('miss');
  const counts = {};

  t.forEach((ch, i) => {
    if (isSeparator(ch)) {
      result[i] = ch;
      return;
    }
    if (g[i] === ch) {
      result[i] = 'hit';
    } else {
      counts[ch] = (counts[ch] || 0) + 1;
    }
  });

  t.forEach((ch, i) => {
    if (result[i] !== 'miss') return;
    const letter = g[i];
    if (letter && !isSeparator(letter) && counts[letter]) {
      result[i] = 'near';
      counts[letter] -= 1;
    }
  });

  return result;
};

const padGuess = (guess, target) => {
  const t = target.toUpperCase();
  const out = [];
  let gi = 0;
  const g = guess.toUpperCase();
  for (let i = 0; i < t.length; i++) {
    if (isSeparator(t[i])) {
      out.push(t[i]);
    } else {
      out.push(g[gi] || '');
      gi += 1;
    }
  }
  return out;
};

const rowView = ({ guess, target, scored, revealedIdx, isCurrent }) => {
  const chars = padGuess(guess, target);
  return chars.map((ch, i) => {
    if (isSeparator(target[i])) return { char: target[i], state: 'empty' };
    if (scored) {
      return { char: ch || '', state: scored[i] || 'miss' };
    }
    if (revealedIdx?.has(i)) {
      return { char: target[i].toUpperCase(), state: 'reveal' };
    }
    if (isCurrent) {
      return { char: ch || '', state: ch ? 'filled' : 'empty' };
    }
    return { char: '', state: 'empty' };
  });
};

const MAX_COLS_PER_LINE = 12;

// Split the target into visual chunks so very long names wrap onto multiple lines.
// Prefer breaking just after a space separator near the end of the window.
const chunkTarget = (chars) => {
  const chunks = [];
  let start = 0;
  while (start < chars.length) {
    let end = Math.min(start + MAX_COLS_PER_LINE, chars.length);
    if (end < chars.length) {
      let breakAt = -1;
      for (let j = end; j > start + 4; j--) {
        if (chars[j - 1] === ' ') {
          breakAt = j;
          break;
        }
      }
      if (breakAt > 0) end = breakAt;
    }
    chunks.push([start, end]);
    start = end;
  }
  return chunks;
};

const LetterGrid = ({ target, attempts, animatingRow, won }) => {
  const upperTarget = target.toUpperCase();
  const chars = upperTarget.split('');
  const chunks = chunkTarget(chars);

  // Only render submitted attempts — no empty placeholder rows.
  const rows = attempts.map((a) =>
    rowView({ guess: a, target: upperTarget, scored: scoreGuess(a, upperTarget) })
  );

  if (rows.length === 0) return null;

  const widestChunk = chunks.reduce((m, [s, e]) => Math.max(m, e - s), 0);
  const maxWidth = Math.min(widestChunk * 56 + (widestChunk - 1) * 6, 640);

  return (
    <div className="mx-auto w-full" style={{ maxWidth }}>
      <div className="flex flex-col gap-3">
        {rows.map((row, rIdx) => (
          <div
            key={rIdx}
            className={cn(
              'flex flex-col gap-1.5',
              won && rIdx === attempts.length - 1 && 'animate-win-bounce'
            )}
            style={{ perspective: 600 }}
          >
            {chunks.map(([s, e], ci) => {
              const slice = row.slice(s, e);
              const chunkChars = chars.slice(s, e);
              // Pad short chunks with invisible filler cells so tile widths stay
              // consistent across all sub-rows of a given row.
              const pad = widestChunk - slice.length;
              const cols = [
                ...chunkChars.map((c) => (isSeparator(c) ? '0.35fr' : '1fr')),
                ...Array(pad).fill('1fr'),
              ].join(' ');
              return (
                <div key={ci} className="grid gap-1.5" style={{ gridTemplateColumns: cols }}>
                  {slice.map((cell, i) => (
                    <Tile
                      key={i}
                      char={cell.char}
                      state={cell.state}
                      animate={rIdx === animatingRow}
                      delay={(s + i) * 60}
                    />
                  ))}
                  {Array.from({ length: pad }).map((_, i) => (
                    <span key={`pad-${i}`} aria-hidden />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LetterGrid;
